import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Text,
} from "@chakra-ui/react";
import { Button, IconButton, IconButtonProps } from "@chakra-ui/button";
import { VStack } from "@chakra-ui/layout";
import { DeleteIcon } from "@chakra-ui/icons";
import { PlusIcon } from "../../../styles/icons";

interface AddRemoveAccordionFieldProps<T> {
  label: string;
  fieldsName: string;
  fields: T[] | undefined;
  newField: T;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  accordionButton: (
    label: string,
    fieldsName: string,
    fields: T[] | null | undefined,
    field: T,
    fieldIdx: number,
    DeleteButton: ({
      idx,
      ...iconProps
    }: {
      idx: number;
    } & IconButtonProps) => JSX.Element
  ) => JSX.Element;
  children: (
    label: string,
    fieldsName: string,
    fields: T[] | null | undefined,
    field: T,
    fieldIdx: number,
    DeleteButton: ({
      idx,
      ...iconProps
    }: {
      idx: number;
    } & IconButtonProps) => JSX.Element
  ) => JSX.Element;
}

export function AddRemoveAccordionField<T>({
  label,
  fieldsName,
  fields,
  newField,
  setFieldValue,
  accordionButton,
  children,
}: AddRemoveAccordionFieldProps<T>) {
  const addField = (scrollTo?: boolean) => {
    if (!fields) {
      fields = [];
    }
    fields.push(newField);
    setFieldValue(fieldsName, fields);
    if (scrollTo)
      setTimeout(
        () =>
          document
            .querySelector(`.${label}-${fields?.length! - 1}`)
            ?.scrollIntoView({ behavior: "smooth" }),
        500
      );
  };

  if (!fields || (fields?.length ?? 0) == 0) {
    return (
      <VStack w="100%" p={4} spacing={2}>
        <Text fontStyle="italic" fontSize="sm">
          {label} has not been set.
        </Text>
        <Button
          size="sm"
          leftIcon={<PlusIcon />}
          onClick={() => addField()}
          colorScheme="cherryButton"
        >
          {label}
        </Button>
      </VStack>
    );
  }

  const deleteButton = ({
    idx,
    ...iconProps
  }: { idx: number } & IconButtonProps) => (
    <IconButton
      title={`Delete ${label}.`}
      icon={<DeleteIcon />}
      size="xs"
      bgColor="cherryGrey.160"
      onClick={() => {
        fields?.splice(idx, 1);
        setFieldValue(fieldsName, fields);
      }}
      {...iconProps}
    />
  );

  return (
    <>
      <Button
        leftIcon={<PlusIcon />}
        onClick={() => addField(true)}
        colorScheme="cherryButton"
        alignSelf="flex-end"
        size="sm"
      >
        {label}
      </Button>

      <Accordion w="100%" allowToggle variant="custom-field">
        <VStack w="100%" alignItems="flex-start">
          {fields.map((field, fieldIdx) => (
            <AccordionItem
              w="100%"
              key={fieldIdx}
              className={`${label}-${fieldIdx}`}
            >
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton textAlign="left" gap={4}>
                      {accordionButton(
                        label,
                        fieldsName,
                        fields,
                        field,
                        fieldIdx,
                        deleteButton
                      )}
                    </AccordionButton>
                  </h2>
                  <AccordionPanel width="100%">
                    {isExpanded &&
                      children(
                        label,
                        fieldsName,
                        fields,
                        field,
                        fieldIdx,
                        deleteButton
                      )}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </VStack>
      </Accordion>
    </>
  );
}
